import { useMutation, type UseMutationResult } from "@tanstack/react-query";
import { useApiClient } from "@shell/apiClientContext";
import type { RecognizeAssetFromPhotoResponse } from "../types";
import { recognizeAssetFromPhoto } from "./api";
import { preprocessImageForUpload } from "./preprocessImageForUpload";

/**
 * Mutation that turns a raw camera/gallery `File` into a structured
 * recognition response. Composition order matters: we resize+re-encode
 * BEFORE the network call so the upload payload is small and the format
 * is JPEG (rather than HEIC, which the .NET image pipeline rejects).
 *
 * No `onSuccess` cache invalidation here — recognition is a stateless,
 * read-only-looking POST (it doesn't mutate any persisted asset). The
 * caller decides what to do with the suggestion.
 */
export function usePhotoFill(): UseMutationResult<RecognizeAssetFromPhotoResponse, Error, File> {
    const client = useApiClient();
    return useMutation({
        mutationFn: async (file: File) => {
            const preprocessed = await preprocessImageForUpload(file);
            return recognizeAssetFromPhoto(client, preprocessed);
        },
    });
}
